import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { underlineSlot, wordBank } from '../kits/slotKit';
import type { PrimitiveSpec, TextMeasurer, TextSpec } from '../kits/specs';
import { estimateMeasure, translateSpecs, TEXT_BOX_HEIGHT } from '../kits/specs';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ContentSourceSpec, StandardContentPayload, ToolRecipe } from '../types';

/**
 * Cloze sentence + word bank — a sentence with one or more blanks to fill from
 * a bank of choices below (CATALOG F2, slotKit.underlineSlot + wordBank).
 *
 * THE SLOT-DERIVATION LAW: the blanks ARE the "___" markers in the sentence
 * param. generate() splits the sentence on each run of underscores, drops an
 * underlineSlot at every split, and flows the plain text runs between them —
 * so editing the sentence in the panel (adding/removing a "___") re-derives
 * the slots on the next regenerate. Two markers → two slots, always.
 *
 * THE ANSWER PAIRING + PRINT-KEY LAW: the bank words fill the blanks in order
 * (blank 1 ← word 1), and each answer text is ALWAYS emitted — visible:false
 * when showAnswers is off — so the answer key is one toggle. Extra bank words
 * beyond the blank count stay in the box as distractors; that is a real cloze,
 * not a bug. The word bank wraps its chips inside a titled frame (wordBank's
 * wrap law), so the box always contains its words.
 *
 * On-canvas editing is the panel here (Apply regenerates in place, one undo
 * entry): neither a sentence FRAGMENT nor a bank CHIP is a whole scalar param,
 * so the generic {kind:'param'} dblclick tag — which round-trips one text to
 * one param — does not apply. The sentence and bank are single fields in the
 * form.
 *
 * Emits FabricText + Line + Rect only, grouped and tagged {tool,version,params}
 * — plain fabric JSON, vector at any export dpi by construction.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the handwriting.ts pattern).
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty string is a MEANINGFUL value here (= no bank / text). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

function makeMeasurer(family: string): TextMeasurer {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return estimateMeasure;
    return (text, fontSize) => {
      ctx.font = `${fontSize}px "${family}", sans-serif`;
      const w = ctx.measureText(text).width;
      return Number.isFinite(w) && w >= 0 ? w : estimateMeasure(text, fontSize);
    };
  } catch {
    return estimateMeasure;
  }
}

// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------

const INK = '#1e293b';
const ANSWER_INK = '#dc2626';
const DEFAULT_FONT = 'Arial';
const FONT_SIZE = 44;

/** A run of 2+ underscores is one blank — the canonical marker is "___". */
const BLANK = /_{2,}/;
/** Punctuation that hugs the preceding word/blank rather than taking a space. */
const HUGS_LEFT = /^[.,;:!?)\]}»”’%]/;

// ---------------------------------------------------------------------------
// Smart-fill seam (SM-H) — build a cloze passage from a standard's vocabulary,
// blanking each term inside its own example sentence.
// ---------------------------------------------------------------------------

/** The canonical blank marker: three underscores, a member of generate()'s
 *  /_{2,}/ blank class and identical to the default sentence's markers. */
const BLANK_MARKER = '___';
/** Passage cap. Cloze flows on ONE line, so a handful of blanks stays legible;
 *  each qualifying vocab item contributes one sentence + one blank. */
const MAX_BLANKS = 4;
/** Total word-bank size (answers first, then distractors) — a usable box. */
const MAX_BANK = 8;

/** Escape a term for literal use inside a RegExp. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * First WHOLE-WORD, case-insensitive occurrence of `term` in `text`. Whole-word
 * here = not flanked by an ASCII letter/digit, which — unlike \b — stays
 * reliable for multi-word ("unauthorized access") and hyphenated terms. Returns
 * the match span, or null when the term never appears as a whole word (so a
 * "Banks encrypt…" example is correctly NOT a home for the term "encryption").
 */
function findTermSpan(text: string, term: string): { start: number; end: number } | null {
  const t = term.trim();
  if (!t || !text) return null;
  const re = new RegExp(`(?<![A-Za-z0-9])${escapeRegExp(t)}(?![A-Za-z0-9])`, 'i');
  const m = re.exec(text);
  return m ? { start: m.index, end: m.index + m[0].length } : null;
}

/** Replace the matched span in `text` with the blank marker (mid-sentence). */
function blankSpan(text: string, span: { start: number; end: number }): string {
  return text.slice(0, span.start) + BLANK_MARKER + text.slice(span.end);
}

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  // vocabulary is the source; examples is the fallback well.
  wants: ['vocabulary', 'examples'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    // Merge INTO a copy so the educator's styling params (color/font/size,
    // showAnswers) are never dropped; only sentence/bank/provenance are touched,
    // and sentence/bank ONLY when real content lands (no off-topic padding).
    const next = { ...current };
    const vocab = Array.isArray(payload?.vocabulary) ? payload.vocabulary : [];

    // A cloze unit = one sentence carrying exactly one blank, plus its answer.
    const units: Array<{ sentence: string; answer: string }> = [];
    const usedTerms = new Set<string>();

    // PRIMARY: vocab items whose OWN `example` sentence contains the term.
    for (const v of vocab) {
      if (units.length >= MAX_BLANKS) break;
      const term = (v?.term ?? '').trim();
      const example = (v?.example ?? '').trim();
      if (!term || !example) continue;
      const key = term.toLowerCase();
      if (usedTerms.has(key)) continue;
      const span = findTermSpan(example, term);
      if (!span) continue;
      units.push({ sentence: blankSpan(example, span), answer: term });
      usedTerms.add(key);
    }

    // FALLBACK (only when PRIMARY found nothing): scan the examples bucket for a
    // sentence containing any vocab term and blank the earliest such term.
    if (units.length === 0) {
      const exBucket = Array.isArray(payload?.examples) ? payload.examples : [];
      const terms = vocab.map((v) => (v?.term ?? '').trim()).filter(Boolean);
      for (const ex of exBucket) {
        if (units.length >= MAX_BLANKS) break;
        const text = (ex?.text ?? '').trim();
        if (!text) continue;
        let best: { term: string; span: { start: number; end: number } } | null = null;
        for (const term of terms) {
          if (usedTerms.has(term.toLowerCase())) continue;
          const span = findTermSpan(text, term);
          if (span && (!best || span.start < best.span.start)) best = { term, span };
        }
        if (!best) continue;
        units.push({ sentence: blankSpan(text, best.span), answer: best.term });
        usedTerms.add(best.term.toLowerCase());
      }
    }

    // Nothing usable → leave sentence/bank UNCHANGED (honesty); still stamp
    // provenance below. When content DID land, write both:
    if (units.length > 0) {
      // The word bank pairs bank[i] → blank[i] (cloze's ANSWER PAIRING LAW), so
      // the correct answers lead IN BLANK ORDER; the standard's OTHER vocab
      // terms follow as distractors (real terms only — never fabricated),
      // deduped and capped to a usable box.
      const answers = units.map((u) => u.answer);
      const seen = new Set(answers.map((a) => a.toLowerCase()));
      const distractors: string[] = [];
      for (const v of vocab) {
        const t = (v?.term ?? '').trim();
        const k = t.toLowerCase();
        if (!t || seen.has(k)) continue;
        seen.add(k);
        distractors.push(t);
      }
      next.sentence = units.map((u) => u.sentence).join(' ');
      next.bank = [...answers, ...distractors].slice(0, MAX_BANK).join(', ');
    }

    // Provenance (declared advanced schema fields — survive regenerate; advanced
    // so a provenance-only change doesn't misfire the modal's "changed" check).
    if (payload?.standard?.code) next.sourceStandardCode = payload.standard.code;
    if (payload?.standard?.id) next.sourceStandardId = payload.standard.id;
    return next;
  },
};

const cloze: ToolRecipe = {
  id: 'cloze',
  domain: 'ela',
  tags: ['cloze', 'word-bank', 'vocabulary', 'reading', 'fill-in-blank', 'worksheet'],
  featured: true,
  name: 'Cloze sentence + word bank',
  category: 'practice',
  version: 1,
  description:
    'A sentence with blanks to fill from a word bank below. Type "___" anywhere in the sentence to make a blank; the bank words answer them in order.',
  defaults: {
    sentence: 'A ___ has eight legs, but an ___ has six.',
    bank: 'spider, insect',
    showAnswers: false,
    color: INK,
    fontFamily: DEFAULT_FONT,
    fontSize: FONT_SIZE,
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    {
      key: 'sentence',
      label: 'Sentence',
      type: 'multiline',
      help: 'Use "___" (three underscores) wherever you want a blank.',
    },
    {
      key: 'bank',
      label: 'Word bank',
      type: 'text',
      help: 'The choices, comma-separated. They fill the blanks in order; extras become distractors.',
    },
    {
      key: 'showAnswers',
      label: 'Show the answers (answer key)',
      type: 'boolean',
      help: 'On prints each answer above its blank; off leaves the blanks empty.',
    },
    { key: 'color', label: 'Color', type: 'color', section: 'Style' },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Style' },
    {
      key: 'fontSize',
      label: 'Text size',
      type: 'number',
      min: 12,
      max: 200,
      step: 2,
      section: 'Style',
    },
    { key: 'sourceStandardCode', label: 'Source standard code', type: 'text', advanced: true, section: 'Style', help: 'Set automatically when filled from a standard.' },
    { key: 'sourceStandardId', label: 'Source standard id', type: 'text', advanced: true, section: 'Style', help: 'Set automatically when filled from a standard.' },
  ],
  contentSource,
  async generate(params) {
    const sentence = rawStr(params.sentence, '');
    const bankRaw = rawStr(params.bank, '');
    const showAnswers = bool(params.showAnswers, false);
    const color = str(params.color, INK);
    const family = str(params.fontFamily, DEFAULT_FONT);
    const fontSize = clamp(num(params.fontSize, FONT_SIZE), 6, 400);

    const measure = makeMeasurer(family);
    const gap = fontSize * 0.32;
    const boxH = fontSize * TEXT_BOX_HEIGHT;

    // The blanks are the "___" markers: N markers → N+1 text runs, N slots.
    const fragments = sentence.split(BLANK);
    const slotCount = fragments.length - 1;
    const words = bankRaw.split(',').map((w) => w.trim()).filter((w) => w.length > 0);

    const sentenceSpecs: PrimitiveSpec[] = [];
    let x = 0;
    let placed = false;
    let lineHeight = boxH;

    fragments.forEach((frag, i) => {
      const text = frag.trim();
      if (text !== '') {
        if (placed && !HUGS_LEFT.test(text)) x += gap;
        sentenceSpecs.push({
          kind: 'text',
          text,
          x,
          y: 0,
          fontSize,
          fontFamily: family,
          fill: color,
          originX: 'left',
          originY: 'top',
        } satisfies TextSpec);
        x += measure(text, fontSize);
        placed = true;
      }
      // A slot follows every fragment except the last.
      if (i < slotCount) {
        if (placed) x += gap;
        const slot = underlineSlot({
          answer: words[i] ?? '',
          showAnswer: showAnswers,
          fontSize,
          color,
          answerColor: ANSWER_INK,
          fontFamily: family,
          measure,
        });
        sentenceSpecs.push(...translateSpecs(slot.specs, x, 0));
        x += slot.width;
        lineHeight = Math.max(lineHeight, slot.height);
        placed = true;
      }
    });

    const sentenceWidth = x;

    // Word bank below the sentence. The box is at least as wide as the
    // sentence (chips wrap inside it — wordBank's wrap law keeps them framed).
    const specs: PrimitiveSpec[] = [...sentenceSpecs];
    if (words.length > 0) {
      const bankWidth = Math.max(sentenceWidth, fontSize * 10);
      const bank = wordBank({
        title: 'Word bank',
        words,
        width: bankWidth,
        fontSize,
        color,
        fontFamily: family,
        measure,
      });
      const bankY = lineHeight + fontSize * 0.9;
      specs.push(...translateSpecs(bank.specs, 0, bankY));
    }

    const group = new Group(assembleSpecs(specs));
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, cloze, params);
  },
};

registerRecipe(cloze);
